/**
 * utils/currency.js
 * Display-only Indonesian thousands-separator formatting for editable currency inputs
 * (Phase T1e — the transaction form's Harga/Subtotal/Total fields). The raw integer is what's
 * always stored/submitted; formatting never touches that value, only what's shown.
 */

/**
 * Parse a currency-formatted or raw string into a whole-Rupiah integer. Strips every
 * non-digit character first (thousands separators, an "Rp" prefix, spaces, a stray decimal
 * comma) — so pasting an already-formatted "Rp 20.000.000" round-trips to 20000000 just like
 * typing "20000000" would. Empty/invalid input -> null (a genuinely UNSET field — matching
 * formatCurrencyInput's own null-vs-zero contract below). A real, non-empty digit string
 * (including the literal digit "0") still parses to a real number; only genuinely
 * empty/unparseable input becomes null. This is a deliberate fix (the two-bucket payment split
 * follow-up redesign's 2nd round): the OLD contract returned 0 for empty input, which meant a
 * field the user had just backspaced to nothing was immediately re-populated with a real 0 —
 * which formatCurrencyInput's own Change 4 fix (0 displays as "0", not blank) would then
 * instantly redraw, so the field could never visually go blank at all.
 */
export function parseCurrencyInput(value) {
  if (value === null || value === undefined) return null;
  const digits = String(value).replace(/[^\d]/g, "");
  if (digits === "") return null;
  const n = parseInt(digits, 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * Format a value (integer or numeric/formatted string) as Indonesian thousands-separated
 * digits for display inside an editable input — no "Rp" prefix (callers render that as a
 * static decorator alongside the input, not part of the editable text), no decimals
 * (whole-Rupiah only).
 *
 * `null`/`undefined`/`""` -> "" (a genuinely UNSET field — no value has ever been typed).
 * A real `0` (or any other real number) formats normally, INCLUDING "0" itself — a deliberate
 * fix (the two-bucket payment split follow-up redesign's Change 4): the old contract collapsed
 * both cases to "", which read as "not filled in" for a value the user deliberately typed as
 * zero (e.g. splitting a payment 100% toward Tagihan & Potongan, 0 toward Barang). Every caller
 * that wants "a freshly added row starts blank" now achieves it by initializing that field to
 * `null`, not `0` — see each initializer's own comment (`addPayment`/`addRow`'s `amount`,
 * `CostItemsEditor`'s `addItem`'s `amount`, the new-line builder's `unitPrice`).
 */
export function formatCurrencyInput(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = parseCurrencyInput(value);
  return n === null ? "" : n.toLocaleString("id-ID");
}
